import type { Edge, Node } from '@xyflow/react'
import dagre from '@dagrejs/dagre'
import type { Mailbox, RoutingRule } from '@/lib/email-rules'
import {
  branchAccent,
  formatDestination,
  isCatchAllRule,
  isSystemGuardRule,
  summariseConditions,
  topLevelRules,
} from '@/lib/email-rules'

export type FlowNodeKind =
  | 'feed'
  | 'guard'
  | 'aiIngestor'
  | 'catchAll'
  | 'branch'
  | 'subFilter'

export interface FlowRuleNodeData {
  kind: FlowNodeKind
  rule: RoutingRule
  label: string
  subtitle: string
  destination: string
  enabled: boolean
  accentClass: string
  [key: string]: unknown
}

export interface FlowFeedNodeData {
  kind: 'feed'
  mailbox: Mailbox
  label: string
  address: string
  isDefault: boolean
  [key: string]: unknown
}

export interface FlowAiNodeData {
  kind: 'aiIngestor'
  label: string
  [key: string]: unknown
}

export type FlowNodeData = FlowRuleNodeData | FlowFeedNodeData | FlowAiNodeData

const NODE_WIDTH = 240
const NODE_HEIGHT = 88
const FEED_HEIGHT = 72
const AI_HEIGHT = 64

function ruleNode(
  rule: RoutingRule,
  kind: FlowNodeKind,
): Node<FlowNodeData> {
  return {
    id: `rule-${rule.id}`,
    type: kind,
    position: { x: 0, y: 0 },
    data: {
      kind,
      rule,
      label: rule.name,
      subtitle: summariseConditions(rule.conditions),
      destination: formatDestination(rule),
      enabled: rule.enabled,
      accentClass: branchAccent(rule.name, rule.conditions),
    },
    draggable: kind !== 'catchAll',
  }
}

function collectChildren(rule: RoutingRule): RoutingRule[] {
  return rule.children ?? []
}

export function buildRoutingFlowGraph(input: {
  mailboxes: Mailbox[]
  rules: RoutingRule[]
  mailboxFilter: 'all' | 'global' | string
}): { nodes: Node<FlowNodeData>[]; edges: Edge[] } {
  const { mailboxes, rules, mailboxFilter } = input
  const nodes: Node<FlowNodeData>[] = []
  const edges: Edge[] = []

  const visibleMailboxes =
    mailboxFilter === 'all'
      ? mailboxes.filter((m) => m.isActive)
      : mailboxFilter === 'global'
        ? []
        : mailboxes.filter((m) => m.id === mailboxFilter)

  for (const mb of visibleMailboxes) {
    const id = `feed-${mb.id}`
    nodes.push({
      id,
      type: 'feed',
      position: { x: 0, y: 0 },
      data: {
        kind: 'feed',
        mailbox: mb,
        label: mb.label,
        address: mb.address,
        isDefault: mb.isDefault,
      },
      draggable: false,
      selectable: false,
    })
  }

  if (mailboxFilter === 'global' || (mailboxFilter === 'all' && visibleMailboxes.length === 0)) {
    nodes.push({
      id: 'feed-global',
      type: 'feed',
      position: { x: 0, y: 0 },
      data: {
        kind: 'feed',
        mailbox: {
          id: 'global',
          address: 'all mailboxes',
          label: 'All inbound feeds',
          description: null,
          isDefault: true,
          isActive: true,
        },
        label: 'All mailboxes',
        address: 'Web form · webhooks · Postmark',
        isDefault: true,
      },
      draggable: false,
      selectable: false,
    })
  }

  const aiId = 'ai-ingestor'
  nodes.push({
    id: aiId,
    type: 'aiIngestor',
    position: { x: 0, y: 0 },
    data: { kind: 'aiIngestor', label: 'AI Ingestor' },
    draggable: false,
    selectable: false,
  })

  for (const feed of nodes.filter((n) => n.type === 'feed')) {
    edges.push({
      id: `e-${feed.id}-${aiId}`,
      source: feed.id,
      target: aiId,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#6366f1', strokeWidth: 2 },
    })
  }

  const roots = topLevelRules(rules)
  const guards = roots.filter(isSystemGuardRule)
  const catchAll = roots.find(isCatchAllRule)
  const branches = roots.filter(
    (r) => !isCatchAllRule(r) && !isSystemGuardRule(r),
  )

  for (const guard of guards) {
    const gNode = ruleNode(guard, 'guard')
    nodes.push(gNode)
    edges.push({
      id: `e-${aiId}-${gNode.id}`,
      source: aiId,
      target: gNode.id,
      type: 'smoothstep',
      style: { stroke: '#94a3b8' },
    })
  }

  if (catchAll) {
    const cNode = ruleNode(catchAll, 'catchAll')
    nodes.push(cNode)
    edges.push({
      id: `e-${aiId}-${cNode.id}`,
      source: aiId,
      target: cNode.id,
      type: 'smoothstep',
      style: { stroke: '#64748b', strokeDasharray: '6 4' },
      label: 'else',
      labelStyle: { fontSize: 10, fill: '#64748b' },
    })
  }

  for (const branch of branches) {
    const bNode = ruleNode(branch, 'branch')
    nodes.push(bNode)
    edges.push({
      id: `e-${aiId}-${bNode.id}`,
      source: aiId,
      target: bNode.id,
      type: 'smoothstep',
      style: { stroke: '#4f46e5', strokeWidth: 2 },
    })

    for (const child of collectChildren(branch)) {
      const sNode = ruleNode(child, 'subFilter')
      nodes.push(sNode)
      edges.push({
        id: `e-${bNode.id}-${sNode.id}`,
        source: bNode.id,
        target: sNode.id,
        type: 'smoothstep',
        style: { stroke: '#818cf8' },
      })

      for (const grandchild of collectChildren(child)) {
        const gcNode = ruleNode(grandchild, 'subFilter')
        nodes.push(gcNode)
        edges.push({
          id: `e-${sNode.id}-${gcNode.id}`,
          source: sNode.id,
          target: gcNode.id,
          type: 'smoothstep',
          style: { stroke: '#a5b4fc' },
        })
      }
    }
  }

  return { nodes, edges }
}

export function layoutRoutingGraph(
  nodes: Node<FlowNodeData>[],
  edges: Edge[],
): Node<FlowNodeData>[] {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({
    rankdir: 'TB',
    nodesep: 56,
    ranksep: 72,
    marginx: 40,
    marginy: 40,
  })

  for (const node of nodes) {
    const h =
      node.type === 'feed'
        ? FEED_HEIGHT
        : node.type === 'aiIngestor'
          ? AI_HEIGHT
          : NODE_HEIGHT
    const w = node.type === 'aiIngestor' ? 320 : NODE_WIDTH
    g.setNode(node.id, { width: w, height: h })
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target)
  }

  dagre.layout(g)

  return nodes.map((node) => {
    const pos = g.node(node.id)
    const h =
      node.type === 'feed'
        ? FEED_HEIGHT
        : node.type === 'aiIngestor'
          ? AI_HEIGHT
          : NODE_HEIGHT
    const w = node.type === 'aiIngestor' ? 320 : NODE_WIDTH
    return {
      ...node,
      position: {
        x: pos.x - w / 2,
        y: pos.y - h / 2,
      },
    }
  })
}

const LAYOUT_STORAGE_KEY = 'ovation-routing-flow-positions'

export function loadSavedPositions(
  artistId: string,
  mailboxKey: string,
): Record<string, { x: number; y: number }> {
  try {
    const raw = localStorage.getItem(`${LAYOUT_STORAGE_KEY}:${artistId}:${mailboxKey}`)
    if (!raw) return {}
    return JSON.parse(raw) as Record<string, { x: number; y: number }>
  } catch {
    return {}
  }
}

export function saveNodePosition(
  artistId: string,
  mailboxKey: string,
  nodeId: string,
  position: { x: number; y: number },
) {
  const key = `${LAYOUT_STORAGE_KEY}:${artistId}:${mailboxKey}`
  const current = loadSavedPositions(artistId, mailboxKey)
  current[nodeId] = position
  localStorage.setItem(key, JSON.stringify(current))
}

export function applySavedPositions(
  nodes: Node<FlowNodeData>[],
  saved: Record<string, { x: number; y: number }>,
): Node<FlowNodeData>[] {
  return nodes.map((n) =>
    saved[n.id] ? { ...n, position: saved[n.id]! } : n,
  )
}

export function clearSavedLayout(artistId: string, mailboxKey: string) {
  localStorage.removeItem(`${LAYOUT_STORAGE_KEY}:${artistId}:${mailboxKey}`)
}

export function ruleIdFromNodeId(nodeId: string): string | null {
  return nodeId.startsWith('rule-') ? nodeId.slice(5) : null
}
